import React, { useContext, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { Box, Dialog, DialogContent, InputAdornment, Link, Stack, TextField, Typography } from '@mui/material';

import { Mail, Lock, LogIn } from 'lucide-react';

import { LoadingButton } from '../Loaders';
import Button from '@mui/material/Button';
import PropTypes from 'prop-types';
import { object, string } from 'yup';

import Axios from '../../Assets/Axios/Axios';

import { COLORS } from '../../Constant/COLORS';
import { USER_TYPES } from '../../Constant/Enums';

import AuthContext from '../../Context/AuthContext';
import ToastContext from '../../Context/ToastContext';

import ForgotPassword from './ForgotPassword';

import { getClearedErrorObj } from '../../Helpers';

import PasswordField from '../PasswordField';

import { CurrentUserQuery } from '../../Server/Query';

import { TypoPoppins } from '../Typo';

import Firebase from '../../lib/firebase';
import { signOutFirebaseUser } from '../../lib/firebaseClients';
import { localStorageData, LOGOUT } from '../../lib/Token';

const Login = ({ onClickSignUpNowText, ...props }) => {
  const { setUser } = useContext(AuthContext);
  const { showToastMessage } = useContext(ToastContext);
  const navigate = useNavigate();

  // Professionals authenticate against a separate Firebase project, so send them
  // to the dedicated professional login (which also handles pending/disabled status).
  const handleClickProfessionalSignIn = () => {
    props.onClose();
    navigate('/professional/login');
  };

  const [isLoginDisabled, setIsLoginDisabled] = useState(false);
  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] = useState(false);
  const [inputs, setInputs] = useState({ email: '', password: '' });
  const [error, setError] = useState({ email: null, password: null });

  const handleChange = event => {
    const { name, value } = event.target;
    setInputs(currentState => ({ ...currentState, [name]: value }));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setIsLoginDisabled(true);
    setError(currentErrors => getClearedErrorObj(currentErrors));
    try {
      const validatedInputs = await schema.validate(inputs, { abortEarly: false });

      // 1) Firebase sign-in first. Never reveal whether the email exists.
      const firebaseResponse = await Firebase.signIn(
        validatedInputs.email,
        validatedInputs.password
      );

      if (firebaseResponse.errors) {
        switch (firebaseResponse.errors) {
          case 'auth/wrong-password':
          case 'auth/invalid-credential':
          case 'auth/user-not-found':
            setError(current => ({
              ...current,
              email: 'Email or password is incorrect.',
            }));
            return undefined;
          case 'auth/user-disabled':
            showToastMessage('Your account has been disabled temporarily');
            return undefined;
          default:
            showToastMessage('Something went wrong while trying to login.');
            return undefined;
        }
      }

      // 2) Mark session so Axios attaches the user-project ID token.
      localStorageData({ type: USER_TYPES.USER });

      // 3) Resolve the DB row via the authenticated currentUser query.
      const dbResponse = await Axios(CurrentUserQuery);

      if (dbResponse?.errors || !dbResponse?.data?.currentUser) {
        await signOutFirebaseUser(USER_TYPES.USER).catch(() => undefined);
        LOGOUT();
        setError(current => ({
          ...current,
          email: 'Email or password is incorrect.',
        }));
        return undefined;
      }

      if (dbResponse.data.currentUser.isActive === false) {
        await signOutFirebaseUser(USER_TYPES.USER).catch(() => undefined);
        LOGOUT();
        showToastMessage('Your account has been disabled', 'info');
        return undefined;
      }

      const user = {
        type: USER_TYPES.USER,
        userID: dbResponse.data.currentUser.id,
      };
      localStorageData(user);
      setUser(user);

      showToastMessage('Hey there, welcome back', 'success');
      props.onClose();
    } catch (error) {
      if (error.name === 'ValidationError') {
        const [validationError] = error.inner;
        setError(currentErrors => ({
          ...getClearedErrorObj(currentErrors),
          [validationError.path]: validationError.message,
        }));
      }
    } finally {
      setIsLoginDisabled(false);
    }
  };

  const handleClickForgotPassword = () => {
    props.onClose();
    setIsForgotPasswordModalOpen(true);
  };

  return (
    <>
      <Dialog
        maxWidth='xs'
        fullWidth
        PaperProps={{ sx: { borderRadius: { xs: '16px', md: '20px' } } }}
        {...props}>
        <DialogContent>
          <Stack
            alignItems='center'
            gap={{ xs: 2, md: 3 }}
            p={{ xs: 1, md: 4 }}
            component='form'
            onSubmit={handleSubmit}>
            <Box textAlign='center'>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  mx: 'auto',
                  mb: 1.5,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${COLORS.teal}, ${COLORS.tealDark})`,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <LogIn size={26} strokeWidth={1.8} />
              </Box>
              <Typography variant='h4' mb={0.5}>
                Sign in to Evento
              </Typography>
              <TypoPoppins color={COLORS.inkSoft}>
                Sign in here to hire professionals
              </TypoPoppins>
            </Box>

            <TextField
              inputMode='email'
              autoComplete='email'
              placeholder='Email'
              value={inputs.email}
              onChange={handleChange}
              name='email'
              inputProps={{ spellCheck: false }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Mail size={18} color={COLORS.inkSoft} strokeWidth={1.8} />
                  </InputAdornment>
                ),
              }}
              error={Boolean(error.email)}
              helperText={error.email}
              fullWidth
            />

            <PasswordField
              autoComplete='current-password'
              placeholder='Password'
              value={inputs.password}
              onChange={handleChange}
              name='password'
              inputProps={{ spellCheck: false }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Lock size={18} color={COLORS.inkSoft} strokeWidth={1.8} />
                  </InputAdornment>
                ),
              }}
              error={Boolean(error.password)}
              helperText={error.password}
              fullWidth
            />

            <Stack alignItems='center' width={1} gap={1}>
              <LoadingButton
                color='blue'
                type='submit'
                isLoading={isLoginDisabled}
                sx={{ width: 6 / 10, mx: 'auto', color: 'white' }}>
                <TypoPoppins color='white' fontWeight={500}>
                  Login
                </TypoPoppins>
              </LoadingButton>

              <Button variant='text' onClick={handleClickForgotPassword}>
                <TypoPoppins>Forgot Password</TypoPoppins>
              </Button>
            </Stack>

            <TypoPoppins>
              Not a member yet?{' '}
              <Link>
                <TypoPoppins
                  sx={{ textDecoration: 'underline' }}
                  fontWeight={600}
                  color={COLORS.teal}
                  component='span'
                  onClick={onClickSignUpNowText}>
                  Sign up now
                </TypoPoppins>
              </Link>
            </TypoPoppins>

            <TypoPoppins>
              Are you a professional?{' '}
              <Link>
                <TypoPoppins
                  sx={{ textDecoration: 'underline' }}
                  fontWeight={600}
                  color={COLORS.teal}
                  component='span'
                  onClick={handleClickProfessionalSignIn}>
                  Sign in here
                </TypoPoppins>
              </Link>
            </TypoPoppins>
          </Stack>
        </DialogContent>
      </Dialog>

      <ForgotPassword
        open={isForgotPasswordModalOpen}
        onClose={() => setIsForgotPasswordModalOpen(false)}
      />
    </>
  );
};

const schema = object({
  email: string().trim().required().email().label('Email'),
  password: string().required().label('Password'),
});

Login.propTypes = {
  onClickSignUpNowText: PropTypes.func,
  onClose: PropTypes.func,
};

export default Login;
